import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCentsToBRL, formatVariants } from '../../utils/currency';
import { getPaymentLabel, getMpMethodLabel, isOnlinePayment, isOrderPaid } from '../../utils/orderPayment';
import { getOrderSourceLabel, getOrderSourceIcon, getOrderSourceColor } from '../../utils/orderSource';
import { isMonitoredStatus, isOrderStale, getOrderAgeMinutes } from '../../utils/orderAlertUtils';
import OrderDetailsModal from './OrderDetailsModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import OrderService from '../../service/order.service';
import { useNotification } from '../../contexts/NotificationContext';
import { ApiContext } from '../../contexts/ApiContext';
import { accountPath } from '../../utils/accountPath';

const FINAL_STATUSES = ['DELIVERED', 'CANCELLED'];

const statusClass = (status) => {
  switch (status) {
    case 'PENDING': return 'status-pending';
    case 'CONFIRMED': return 'status-confirmed';
    case 'PREPARING': return 'status-preparing';
    case 'READY_FOR_DELIVERY': return 'status-ready-for-delivery';
    case 'OUT_FOR_DELIVERY': return 'status-out-for-delivery';
    case 'DELIVERED': return 'status-delivered';
    case 'CANCELLED': return 'status-cancelled';
    default: return '';
  }
};

export const ORDER_TYPE_LABELS = {
  DELIVERY: 'Entrega',
  PICKUP: 'Retirada',
  DINE_IN: 'Mesa',
};

const formatRelativeAge = (minutes) => {
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `há ${hours}h`;
  return `há ${hours}h ${mins}min`;
};

const OrderCard = ({ order, statuses, onChangeStatus, onUpdated, onResolved }) => {
  const navigate = useNavigate();
  const { account } = useContext(ApiContext);
  const [open, setOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showConfirmResolve, setShowConfirmResolve] = useState(false);
  const [ageMinutes, setAgeMinutes] = useState(() => getOrderAgeMinutes(order));
  const { notify } = useNotification();
  const toggle = () => setOpen((v) => !v);

  const isFinal = FINAL_STATUSES.includes(order.status);
  const showAge = isMonitoredStatus(order.status);
  const stale = showAge && isOrderStale(order);

  useEffect(() => {
    if (!showAge) return undefined;
    setAgeMinutes(getOrderAgeMinutes(order));
    const interval = setInterval(() => {
      setAgeMinutes(getOrderAgeMinutes(order));
    }, 30000);
    return () => clearInterval(interval);
  }, [order.id, order.status, order.createdAt, order.updatedAt, showAge]);

  const openResolve = (e) => {
    if (e) e.stopPropagation();
    setShowConfirmResolve(true);
  };

  const doResolve = async () => {
    try {
      await OrderService.resolveOrder(order.id);
      notify('tr', 'success', 'Pedido resolvido', 'icon-check-2');
      if (onResolved) onResolved(order.id);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao resolver pedido';
      notify('tr', 'danger', Array.isArray(msg) ? msg.join(', ') : msg, 'icon-alert-circle-exc');
    }
  };

  useEffect(() => {
    if (order._highlight) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [order._highlight]);

  return (
    <>
    <div
      className={`kanban-card ${statusClass(order.status)} ${animate ? 'highlight' : ''} ${stale ? 'kanban-card--stale' : ''}`}
      style={{ cursor: 'pointer', position: 'relative', paddingBottom: '35px' }}
    >
      <div onClick={toggle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>#{order.id}</strong> — {order.customerName}
            <div className="order-meta">{order.customerPhone}</div>
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'rgba(29,140,248,0.15)', color: '#1d8cf8' }}>
                {ORDER_TYPE_LABELS[order.type] || order.type || 'Entrega'}
                {order.type === 'DINE_IN' && (order.tableName || order.table?.name) ? ` · ${order.tableName || order.table?.name}` : ''}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 600 }}>{formatCentsToBRL(order.totalCents)}</div>
            {order.couponCode && order.discountCents > 0 && (
              <div className="order-meta" style={{ color: '#00c853' }}>
                {order.couponCode} − {formatCentsToBRL(order.discountCents)}
              </div>
            )}
            {showAge && (
              <div className={`order-age ${stale ? 'order-age--stale' : ''}`}>
                {formatRelativeAge(ageMinutes)}
              </div>
            )}
            <div className="order-meta">{new Date(order.createdAt).toLocaleString()}</div>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          {order.items.map((it) => (
            <div key={it.id} style={{ borderTop: '1px dashed #eee', paddingTop: 8, marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>{it.quantity}x {it.name}{it.sizeName ? ` · ${it.sizeName}` : ''}</div>
                <div style={{ color: '#444' }}>{formatCentsToBRL(it.lineTotalCents)}</div>
              </div>
              {it.variants && it.variants.length > 0 && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  {formatVariants(it.variants, true)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: 12 }}>
            <span style={{ color: '#666' }}>{getPaymentLabel(order.payment)}</span>
            {isOnlinePayment(order) && (
              <span style={{
                marginLeft: 6,
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 4,
                background: isOrderPaid(order) ? 'rgba(0, 187, 51, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                color: isOrderPaid(order) ? '#00bb33' : '#ffc107',
              }}>
                {isOrderPaid(order) ? 'PAGO' : 'AGUARDANDO'}
              </span>
            )}
            {isOnlinePayment(order) && isOrderPaid(order) && order.mercadoPagoPaymentId && (
              <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>
                MP: {order.mercadoPagoPaymentId}
              </div>
            )}
          </div>
          <div>
            {onChangeStatus && (
              <select value={order.status} onChange={(e) => onChangeStatus(order, e.target.value)}>
                <option value="PENDING">Pendente</option>
                <option value="CONFIRMED">Confirmado</option>
                <option value="PREPARING">Preparando</option>
                <option value="READY_FOR_DELIVERY">Pronto para entrega</option>
                <option value="OUT_FOR_DELIVERY">Pronto para entrega</option>
                <option value="DELIVERED">Entregue</option>
                <option value="CANCELLED">Cancelado</option>
              </select>
            )}
          </div>
        </div>
      </div>

      <div
        title={getOrderSourceLabel(order)}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          width: '24px',
          height: '24px',
          borderRadius: '4px',
          backgroundColor: order.source === 'AGENT' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(0, 187, 51, 0.15)',
          border: `1px solid ${order.source === 'AGENT' ? 'rgba(124, 58, 237, 0.35)' : 'rgba(0, 187, 51, 0.35)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        <i
          className={`tim-icons ${getOrderSourceIcon(order)}`}
          style={{ fontSize: '11px', color: getOrderSourceColor(order) }}
        />
      </div>
      
      {isFinal && (
        <div
          title="Resolver pedido"
          onClick={openResolve}
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '40px',
            width: '24px',
            height: '24px',
            borderRadius: '4px',
            backgroundColor: 'rgba(0, 187, 51, 0.2)',
            border: '1px solid rgba(0, 187, 51, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 187, 51, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 187, 51, 0.2)';
          }}
        >
          <i className="tim-icons icon-check-2" style={{ fontSize: '10px', color: '#00bb33' }} />
        </div>
      )}

      <div
        onClick={(e) => {
          e.stopPropagation();
          navigate(accountPath(account?.id, `/pedidos/editar/${order.id}`));
        }}
        style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          width: '24px',
          height: '24px',
          borderRadius: '4px',
          backgroundColor: 'rgba(29, 140, 248, 0.2)',
          border: '1px solid rgba(29, 140, 248, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(29, 140, 248, 0.35)';
          e.currentTarget.style.borderColor = 'rgba(29, 140, 248, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(29, 140, 248, 0.2)';
          e.currentTarget.style.borderColor = 'rgba(29, 140, 248, 0.4)';
        }}
      >
        <i className="tim-icons icon-pencil" style={{ fontSize: '10px', color: '#1d8cf8' }} />
      </div>
    </div>
    <OrderDetailsModal isOpen={open} toggle={toggle} order={order} statuses={statuses} onUpdated={onUpdated} />
    <ConfirmDeleteModal
      isOpen={showConfirmResolve}
      toggle={() => setShowConfirmResolve(false)}
      title="Resolver pedido"
      message={`Resolver o pedido #${order.id}? Ele sairá da lista.`}
      btnConfirm="Resolver"
      btnCancel="Cancelar"
      confirmColor="success"
      onConfirm={doResolve}
    />
    </>
  );
};

export default OrderCard;
